import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { DataTable } from '@/components/common/DataTable/DataTable';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useUsers,
  useUserRoles,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useToggleUserActive,
  User,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/api/hooks/useUsers';
import { usePermissions } from '@/hooks/use-permissions';
import { Checkbox } from '@/components/ui/checkbox';
import { isValidEmail } from '@/lib/utils';
import {
  Plus,
  Search,
  Filter,
  Users,
  UserCheck,
  UserX,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
  Power,
  Shield,
  KeyRound,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ResetPasswordDialog } from '@/pages/clients-hub/sections/ResetPasswordDialog';

export default function UsersPage() {
  const { t } = useTranslation(['catalogs', 'common']);
  const { canCreate, canEdit, canDelete, canView } = usePermissions();

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Dialog state
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  // The email error waits for the field to lose focus, so it does not flag
  // a half-typed address.
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<User | null>(null);

  // Role selection state
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  // Form state. The backend SecurityUser has no firstName/lastName/email
  // columns — only `description` (used as the full name) — so we model the
  // form accordingly. isAdmin/mfaEnabled/blocked are carried through on edit
  // so saves don't silently clobber flags that aren't rendered as inputs.
  const [formData, setFormData] = useState<{
    username: string;
    password: string;
    description: string;
    isActive: boolean;
    isAdmin: boolean;
    mfaEnabled: boolean;
    blocked: boolean;
  }>({
    username: '',
    password: '',
    description: '',
    isActive: true,
    isAdmin: false,
    mfaEnabled: false,
    blocked: false,
  });

  // Fetch users from API
  const {
    data: usersResponse,
    isLoading,
    error,
  } = useUsers({
    search: searchTerm || undefined,
    roleId: roleFilter ? parseInt(roleFilter) : undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    page,
    pageSize,
    backofficeOnly: true,
  });

  // Fetch roles for dropdown
  const { data: roles } = useUserRoles({ enabled: canView('roles') });

  // Mutations
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();
  const toggleActiveMutation = useToggleUserActive();

  // Calculate stats
  const stats = useMemo(() => {
    const items = usersResponse?.items ?? [];
    return {
      total: usersResponse?.totalCount ?? 0,
      active: items.filter((u) => u.isActive).length,
      inactive: items.filter((u) => !u.isActive).length,
    };
  }, [usersResponse]);

  // Handlers
  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        // transformUser sets fullName = apiUser.description — that's the
        // actual editable name stored in the DB.
        description: user.fullName ?? '',
        isActive: user.isActive,
        isAdmin: user.isAdmin,
        mfaEnabled: user.mfaEnabled,
        blocked: user.blocked,
      });
      setSelectedRoleIds(user.roleIds ?? []);
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        description: '',
        isActive: true,
        isAdmin: false,
        mfaEnabled: false,
        blocked: false,
      });
      setSelectedRoleIds([]);
    }
    setUsernameTouched(false);
    setShowUserDialog(true);
  };

  const handleCloseDialog = () => {
    setShowUserDialog(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      description: '',
      isActive: true,
      isAdmin: false,
      mfaEnabled: false,
      blocked: false,
    });
    setSelectedRoleIds([]);
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        const updateData: UpdateUserRequest = {
          id: editingUser.id,
          username: formData.username,
          description: formData.description,
          isActive: formData.isActive,
          // Carry through so the backend doesn't default it to false and
          // silently demote an Administrator on a plain "Save Changes".
          isAdmin: formData.isAdmin,
          mfaEnabled: formData.mfaEnabled,
          blocked: formData.blocked,
          roleIds: selectedRoleIds,
        };
        await updateMutation.mutateAsync(updateData);
      } else {
        await createMutation.mutateAsync({
          username: formData.username.trim(),
          password: formData.password,
          description: formData.description,
          isActive: formData.isActive,
          isAdmin: formData.isAdmin,
          mfaEnabled: formData.mfaEnabled,
          blocked: formData.blocked,
          roleIds: selectedRoleIds,
        } as CreateUserRequest);
      }
      toast.success(
        editingUser
          ? t('users.messages.updated')
          : t('users.messages.created'),
      );
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save user:', error);
      const msg =
        (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
        (error instanceof Error ? error.message : t('common:messages.errorSaving'));
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await deleteMutation.mutateAsync(deleteUser.id);
      toast.success(t('users.messages.deleted'));
      setDeleteUser(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
      const msg =
        (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
        (error instanceof Error ? error.message : t('common:messages.errorSaving'));
      toast.error(msg);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await toggleActiveMutation.mutateAsync({
        id: user.id,
        isActive: !user.isActive,
      });
      toast.success(t('users.messages.updated'));
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      const msg =
        (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
        (error instanceof Error ? error.message : t('common:messages.errorSaving'));
      toast.error(msg);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const hasActiveFilters = searchTerm || roleFilter || statusFilter;

  // Table columns
  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'username',
        header: t('users.table.username'),
        cell: ({ row }) => (
          <div className="font-medium">{row.original.username}</div>
        ),
      },
      {
        accessorKey: 'fullName',
        header: t('users.table.name'),
        cell: ({ row }) => row.original.fullName || `${row.original.firstName} ${row.original.lastName}`,
      },
      {
        accessorKey: 'roleName',
        header: t('users.table.role'),
        cell: ({ row }) => (
          <Badge variant="outline" className="gap-1">
            <Shield className="h-3 w-3" />
            {row.original.roleName}
          </Badge>
        ),
      },
      {
        accessorKey: 'isActive',
        header: t('users.table.status'),
        cell: ({ row }) => (
          <Badge
            className={
              row.original.isActive
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          >
            {row.original.isActive ? t('users.status.active') : t('users.status.inactive')}
          </Badge>
        ),
      },
      {
        accessorKey: 'lastLoginDate',
        header: t('users.table.lastLogin'),
        cell: ({ row }) =>
          row.original.lastLoginDate
            ? format(new Date(row.original.lastLoginDate), "d 'de' MMM yyyy, HH:mm", { locale: es })
            : '-',
      },
      ...((canEdit('users') || canDelete('users')) ? [{
        id: 'actions',
        header: '',
        cell: ({ row }: { row: { original: User } }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('users.table.actions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit('users') && (
                <DropdownMenuItem onClick={() => handleOpenDialog(row.original)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('users.actions.edit')}
                </DropdownMenuItem>
              )}
              {canEdit('users') && (
                <DropdownMenuItem onClick={() => setResetPasswordTarget(row.original)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t('users.form.resetPasswordButton')}
                </DropdownMenuItem>
              )}
              {canEdit('users') && (
                <DropdownMenuItem
                  onClick={() => handleToggleActive(row.original)}
                  disabled={toggleActiveMutation.isPending}
                >
                  <Power className="mr-2 h-4 w-4" />
                  {row.original.isActive ? t('users.actions.deactivate') : t('users.actions.activate')}
                </DropdownMenuItem>
              )}
              {canDelete('users') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setDeleteUser(row.original)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('users.actions.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      } as ColumnDef<User>] : []),
    ],
    [t, toggleActiveMutation.isPending, canEdit, canDelete]
  );

  // Pagination handlers
  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // The username is the sign-in email (forgot-password looks it up as one).
  // Only new users are checked: existing usernames are not editable here.
  const usernameIsEmail = isValidEmail(formData.username);
  const showUsernameError = !editingUser && usernameTouched && formData.username.trim() !== '' && !usernameIsEmail;

  const isFormValid = editingUser
    ? formData.username
    : usernameIsEmail && formData.password;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('users.pageTitle')}</h1>
          <p className="text-muted-foreground">{t('users.pageDescription')}</p>
        </div>
        {canCreate('users') && (
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            {t('users.newUser')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('users.stats.total')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.total}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('users.stats.active')}</CardTitle>
            <UserCheck className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.active}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('users.stats.inactive')}</CardTitle>
            <UserX className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.inactive}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('users.cardTitle')}</CardTitle>
          <CardDescription>{t('users.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('users.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="pl-8"
                />
              </div>
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('common:buttons.filters')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {t('common:status.active')}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border bg-muted/50">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('users.filters.role')}</label>
                  <Select
                    value={roleFilter || 'all'}
                    onValueChange={(v) => {
                      setRoleFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('users.filters.allRoles')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('users.filters.allRoles')}</SelectItem>
                      {roles?.map((role) => (
                        <SelectItem key={role.id} value={String(role.id)}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('users.filters.status')}</label>
                  <Select
                    value={statusFilter || 'all'}
                    onValueChange={(v) => {
                      setStatusFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('users.status.all')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('users.status.all')}</SelectItem>
                      <SelectItem value="active">{t('users.status.active')}</SelectItem>
                      <SelectItem value="inactive">{t('users.status.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    {t('users.filters.clearFilters')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Data Table */}
          {error ? (
            <div className="text-center py-8 text-destructive">
              {t('users.messages.errorLoading')}
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={usersResponse?.items ?? []}
              pageCount={usersResponse?.totalPages ?? 1}
              pageIndex={page - 1}
              pageSize={pageSize}
              onPageChange={(pageIndex) => handlePageChange(pageIndex + 1)}
              onPageSizeChange={handlePageSizeChange}
              totalItems={usersResponse?.totalCount ?? 0}
            />
          )}
        </CardContent>
      </Card>

      {/* User Form Dialog */}
      <Dialog open={showUserDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? t('users.form.editTitle') : t('users.form.createTitle')}</DialogTitle>
            <DialogDescription>
              {editingUser
                ? t('users.form.editDescription')
                : t('users.form.createDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="username">{t('users.form.usernameLabel')}</Label>
              <Input
                id="username"
                type="email"
                inputMode="email"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={t('users.form.usernamePlaceholder')}
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                onBlur={() => setUsernameTouched(true)}
                disabled={!!editingUser}
                aria-invalid={showUsernameError || undefined}
                aria-describedby="username-help"
              />
              {showUsernameError ? (
                <p id="username-help" className="text-destructive text-xs mt-1">
                  {t('users.form.usernameInvalid')}
                </p>
              ) : (
                <p id="username-help" className="text-muted-foreground text-xs mt-1">
                  {t('users.form.usernameHint')}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="description">{t('users.form.nameLabel')}</Label>
              <Input
                id="description"
                placeholder={t('users.form.namePlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            {!editingUser && (
              <div>
                <Label htmlFor="password">{t('users.form.passwordLabel')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('users.form.passwordPlaceholder')}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            )}
            <div>
              <Label>{t('users.form.roleLabel')}</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto rounded-md border p-3">
                {roles?.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={selectedRoleIds.includes(role.id)}
                      onCheckedChange={(checked) => {
                        setSelectedRoleIds((prev) =>
                          checked
                            ? [...prev, role.id]
                            : prev.filter((id) => id !== role.id)
                        );
                      }}
                    />
                    <label htmlFor={`role-${role.id}`} className="text-sm cursor-pointer">
                      {role.name}
                    </label>
                  </div>
                ))}
                {(!roles || roles.length === 0) && (
                  <p className="text-sm text-muted-foreground">{t('users.form.rolePlaceholder')}</p>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="text-sm font-semibold">
                {t('users.form.accessSection')}
              </h4>

              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="isActive" className="font-medium">
                    {t('users.form.isActiveLabel')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('users.form.isActiveHelp')}
                  </p>
                </div>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="isAdmin" className="font-medium">
                    {t('users.form.isAdminLabel')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('users.form.isAdminHelp')}
                  </p>
                </div>
                <Switch
                  id="isAdmin"
                  checked={formData.isAdmin}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isAdmin: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="mfaEnabled" className="font-medium">
                    {t('users.form.mfaEnabledLabel')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('users.form.mfaEnabledHelp')}
                  </p>
                </div>
                <Switch
                  id="mfaEnabled"
                  checked={formData.mfaEnabled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, mfaEnabled: checked })
                  }
                />
              </div>

              {editingUser && (
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="blocked" className="font-medium">
                      {t('users.form.blockedLabel')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('users.form.blockedHelp')}
                    </p>
                  </div>
                  <Switch
                    id="blocked"
                    checked={formData.blocked}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, blocked: checked })
                    }
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? t('common:status.saving')
                : editingUser
                ? t('common:buttons.saveChanges')
                : t('common:buttons.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      {resetPasswordTarget && (
        <ResetPasswordDialog
          open={!!resetPasswordTarget}
          onOpenChange={(open) => !open && setResetPasswordTarget(null)}
          userId={resetPasswordTarget.id}
          userDisplayName={resetPasswordTarget.fullName || resetPasswordTarget.username}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
        title={t('users.dialog.deleteTitle')}
        description={t('users.dialog.deleteConfirm', { username: deleteUser?.username })}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
